import { parseDeviceTypeApiValue } from '../../../NonReact/constants/DeviceTypeConstants'
import { DeviceType } from '../../../NonReact/constants/DeviceTypes'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'
import type { DeviceModalCapabilityFlags } from '../Spec/DeviceModalCapabilityKeys'
import {
    DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID,
    DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY,
    DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS,
    DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID,
    DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID,
    DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS,
    DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD
} from '../Spec/DeviceModalCapabilityKeys'
import {
    DEVICE_MODAL_CAPABILITY_FLAGS_UNMAPPED_DEVICE_TYPE,
    getDeviceModalCapabilityFlagsForDeviceType
} from '../Spec/DeviceModalCapabilityMatrix'

/**
 * <p>Resolves Confluence matrix capability flags for a device type, including MS Teams Call Queue
 * disambiguation for shared type {@code 43}, Webex-only primary email for extension endpoints, and
 * Teams/Webex-only telephony user id field, BroadSoft M6-only monitor password field, internal dialling
 * address only for BroadWorks / Webex Calling / MS Teams (all device types), ID on telephony server owner only
 * for BroadWorks and Webex Calling (all device types), and Siemens-hidden ID-on-telephony-server field for all
 * other TelSys types.</p>
 *
 * @param deviceType - Backend {@code DeviceType} string from the form or API.
 * @param telSysType - Telephony module type from the admin list scope (when known).
 * @returns Capability flags; falls back to {@link DEVICE_MODAL_CAPABILITY_FLAGS_UNMAPPED_DEVICE_TYPE} when unmapped.
 */
export const resolveDeviceModalCapabilityFlags = (
    deviceType: string,
    telSysType?: TelSysTypes
): DeviceModalCapabilityFlags => {
    const deviceTypeId: DeviceType = parseDeviceTypeApiValue(deviceType)
    const resolved: OnlyNullable<DeviceModalCapabilityFlags> = getDeviceModalCapabilityFlagsForDeviceType(deviceTypeId, {
        isMsTeamsCallQueuePresentation: telSysType === TelSysTypes.MS_TEAMS
    })

    const base: DeviceModalCapabilityFlags = resolved ?? DEVICE_MODAL_CAPABILITY_FLAGS_UNMAPPED_DEVICE_TYPE

    let flags: DeviceModalCapabilityFlags = base

    const primaryEmailAllowed: boolean =
        telSysType === TelSysTypes.WEBEX_CALLING && deviceTypeId === DeviceType.ExtensionOrEndpoint

    if (primaryEmailAllowed !== flags[DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS]) {
        flags = { ...flags, [DEVICE_MODAL_CAPABILITY_PRIMARY_EMAIL_ADDRESS]: primaryEmailAllowed }
    }

    const intDiallingAllowed: boolean =
        telSysType === TelSysTypes.BROADSOFT_BROADWORKS ||
        telSysType === TelSysTypes.WEBEX_CALLING ||
        telSysType === TelSysTypes.MS_TEAMS

    if (intDiallingAllowed !== flags[DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS]) {
        flags = { ...flags, [DEVICE_MODAL_CAPABILITY_INT_DIALLING_ADDRESS]: intDiallingAllowed }
    }

    const idOnTelSysOwnerAllowed: boolean =
        telSysType === TelSysTypes.BROADSOFT_BROADWORKS || telSysType === TelSysTypes.WEBEX_CALLING

    if (idOnTelSysOwnerAllowed !== flags[DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID]) {
        flags = { ...flags, [DEVICE_MODAL_CAPABILITY_OWNING_GROUP_ID]: idOnTelSysOwnerAllowed }
    }

    const telSysUserIdMsTeams: boolean = telSysType === TelSysTypes.MS_TEAMS
    const telSysUserIdWebex: boolean = telSysType === TelSysTypes.WEBEX_CALLING

    if (
        telSysUserIdMsTeams !== flags[DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID] ||
        telSysUserIdWebex !== flags[DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY]
    ) {
        flags = {
            ...flags,
            [DEVICE_MODAL_CAPABILITY_MS_TEAMS_USER_ID]: telSysUserIdMsTeams,
            [DEVICE_MODAL_CAPABILITY_CISCO_COMMON_IDENTITY]: telSysUserIdWebex
        }
    }

    const telsysMonitorPasswordAllowed: boolean = telSysType === TelSysTypes.BROADSOFT_M6

    if (telsysMonitorPasswordAllowed !== flags[DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD]) {
        flags = { ...flags, [DEVICE_MODAL_CAPABILITY_TELSYS_MONITOR_PASSWORD]: telsysMonitorPasswordAllowed }
    }

    const idOnTelSysAllowed: boolean = telSysType !== TelSysTypes.SIEMENS_HIPATH_3000

    if (idOnTelSysAllowed !== flags[DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID]) {
        flags = { ...flags, [DEVICE_MODAL_CAPABILITY_BROADWORKS_ID_OR_UPN_WEBEX_USER_ID]: idOnTelSysAllowed }
    }

    return flags
}
